import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useLocalAuth } from '@/auth/LocalAuthContext'
import api from '@/services/api'
import type { Loan } from '@/types'
import { toCamelCase } from '@/utils/transform'
import {
  attachLiveTriggers,
  deriveTriggerAlerts,
  type TriggerAlert,
  type TriggerRow,
} from '@/utils/triggerAlerts'
import { isLiveBookAuthReady } from './useDemoDataAccess'

export const OPEN_TRIGGERS_QUERY_KEY = ['triggers', 'open'] as const

export interface LiveTriggerAlerts {
  alerts: TriggerAlert[]
  overdueCount: number
  /** False when the reader's role cannot see triggers (the API answered 403,
   *  or the role's module permissions exclude Asset Management). The caller
   *  says so instead of showing a reassuring 0. */
  available: boolean
  isLoading: boolean
  /** The read failed for a reason other than a refusal (the API was down, a 5xx). The caller says
   *  the triggers could not be read; it never turns that into "0 open". */
  failed: boolean
}

/** `GET /triggers/`, gated the way the API gates it: a role without Asset
 *  Management view is not asked, and a 403 is not retried. One query key, so
 *  every consumer shares one request. */
function useOpenTriggerRows(enabled = true) {
  const { localToken, localUser } = useLocalAuth()
  const authReady = isLiveBookAuthReady(localToken)
  // Presentation only — the API is authoritative. Unknown permissions mean
  // "ask the API" rather than assume a refusal.
  const actions = localUser?.permissions?.['asset-management']
  const roleMaySee = Boolean(localUser?.is_super_admin) || !actions || actions.includes('view')

  const query = useQuery({
    queryKey: OPEN_TRIGGERS_QUERY_KEY,
    // Trailing slash matches FastAPI's route; `/triggers` 307s and a browser
    // following the bounce can drop the bearer.
    queryFn: async () => toCamelCase<TriggerRow[]>((await api.get('/triggers/')).data),
    enabled: enabled && authReady && roleMaySee,
    staleTime: 60_000,
    refetchInterval: 120_000,
    retry: (failures, error) =>
      !(isAxiosError(error) && error.response?.status === 403) && failures < 2,
  })
  const forbidden = isAxiosError(query.error) && query.error.response?.status === 403
  return {
    rows: Array.isArray(query.data) ? query.data : null,
    available: roleMaySee && !forbidden,
    isLoading: query.isLoading,
    failed: query.isError && !forbidden && !Array.isArray(query.data),
  }
}

/** Open triggers on the loaded live book — the one source for Command
 *  Center's Overdue Triggers tile, its Alert Feed and the notification bell. */
export function useLiveTriggerAlerts(loans: Loan[]): LiveTriggerAlerts {
  const { rows, available, isLoading, failed } = useOpenTriggerRows()
  // Keyed on the loan ids, not the array: a caller whose book has not loaded
  // passes a fresh [] on every render, and alerts must not change identity
  // with it.
  const loanKey = loans.map((loan) => loan.id).join('|')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableLoans = useMemo(() => loans, [loanKey])
  const alerts = useMemo(
    () => deriveTriggerAlerts(rows ?? [], stableLoans),
    [rows, stableLoans],
  )
  return {
    alerts,
    overdueCount: alerts.filter((alert) => alert.overdue).length,
    available,
    isLoading,
    failed,
  }
}

/** The live book with each deal's `/triggers/` rows on `loan.triggers`, for
 *  the rules that read them there (Pipeline exceptions, the Inspector). A demo
 *  book (`isLive` false) keeps its own triggers; until the triggers answer, or
 *  when the role may not see them, the loans pass through unchanged. */
export function useLoansWithLiveTriggers(loans: Loan[], isLive: boolean): Loan[] {
  const { rows } = useOpenTriggerRows(isLive)
  return useMemo(
    () => (isLive && rows ? attachLiveTriggers(loans, rows) : loans),
    [isLive, loans, rows],
  )
}
